import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { sharingApi } from '../../lib/api/sharingApi';
export function UserSearch({ onSelect, existingUserIds, maxShares, currentCount, isLoading }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const timeoutRef = useRef();
    const remainingSlots = maxShares - currentCount - selected.length;
    // Filter out already shared and selected users from results
    const filterUsers = useCallback((users)=>users.filter((u)=>!existingUserIds.includes(u.userId) && !selected.some((s)=>s.userId === u.userId)), [
        existingUserIds,
        selected
    ]);
    // Load default users on mount
    useEffect(()=>{
        const loadDefaultUsers = async ()=>{
            setIsSearching(true);
            try {
                const users = await sharingApi.searchUsers('');
                setResults(filterUsers(users ?? []));
            } finally{
                setIsSearching(false);
            }
        };
        loadDefaultUsers();
    }, [
        filterUsers
    ]);
    const handleInputChange = useCallback((e)=>{
        const value = e.target.value;
        setQuery(value);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(async ()=>{
            setIsSearching(true);
            try {
                const users = await sharingApi.searchUsers(value);
                setResults(filterUsers(users ?? []));
            } finally{
                setIsSearching(false);
            }
        }, 300);
    }, [
        filterUsers
    ]);
    const handleSelectUser = (user)=>{
        if (remainingSlots <= 0) return;
        setSelected([
            ...selected,
            user
        ]);
        setQuery('');
        // Remove selected user from results instead of clearing
        setResults((prev)=>prev.filter((u)=>u.userId !== user.userId));
    };
    const handleRemoveSelected = (userId)=>{
        setSelected(selected.filter((u)=>u.userId !== userId));
    };
    const handleShare = ()=>{
        onSelect(selected.map((u)=>({
                userId: u.userId,
                email: u.email,
                name: u.name
            })));
        setSelected([]);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        "data-testid": Testids.sharing.userSearch,
        children: [
            selected.length > 0 && /*#__PURE__*/ _jsx("div", {
                className: "flex flex-wrap gap-2",
                children: selected.map((user)=>/*#__PURE__*/ _jsxs("span", {
                        className: "flex items-center gap-1 px-2 py-1 bg-primary/10 rounded text-sm",
                        children: [
                            user.name,
                            /*#__PURE__*/ _jsx("button", {
                                onClick: ()=>handleRemoveSelected(user.userId),
                                className: "ml-1 hover:text-primary",
                                "aria-label": `Remove ${user.name}`,
                                children: /*#__PURE__*/ _jsx(X, {
                                    className: "w-3 h-3"
                                })
                            })
                        ]
                    }, user.userId))
            }),
            /*#__PURE__*/ _jsx("input", {
                type: "text",
                value: query,
                onChange: handleInputChange,
                placeholder: "Search by name or email...",
                disabled: remainingSlots <= 0 || isLoading,
                className: "w-full px-3 py-2 border border-input rounded-md text-sm disabled:opacity-50"
            }),
            remainingSlots <= 0 && /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                children: [
                    "Maximum ",
                    maxShares,
                    " shares reached"
                ]
            }),
            isSearching && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Searching..."
            }),
            results.length > 0 && /*#__PURE__*/ _jsx("ul", {
                className: "border border-input rounded-md max-h-48 overflow-y-auto",
                children: results.map((user)=>/*#__PURE__*/ _jsxs("li", {
                        onClick: ()=>handleSelectUser(user),
                        className: "px-3 py-2 hover:bg-muted cursor-pointer border-b last:border-b-0 text-sm",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "font-medium",
                                children: user.name
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: user.email
                            })
                        ]
                    }, user.userId))
            }),
            selected.length > 0 && /*#__PURE__*/ _jsxs("button", {
                onClick: handleShare,
                disabled: isLoading,
                className: "w-full px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50",
                children: [
                    "Share with ",
                    selected.length,
                    " ",
                    selected.length === 1 ? 'person' : 'people'
                ]
            })
        ]
    });
}
